import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, SectionTitle, TextArea, Toggle } from '../../../../components';
import { firstErrorMessage, firstWarningMessage, issuesFor } from '../../issues-by-field';
import type { SiteSeoAi, SiteSeoIssue } from '../../seo-types';
import { LLMS_FACTS_MAX, LLMS_SUMMARY_MAX, PLACEHOLDER_RE } from '../../seo-validation';
import {
  Body,
  ErrorText,
  FieldBlock,
  Head,
  HintText,
  Note,
  Preview,
  PreviewText,
  PreviewTitle,
  ToggleLabel,
  ToggleRow,
} from './AiAssistantsPanel.styles';

export type AiAssistantsPanelProps = {
  ai: SiteSeoAi;
  /** The server's llms.txt as of its last GET or draft PUT answer; the hub builds nothing. */
  llmsPreview: string;
  onChange: (patch: Partial<SiteSeoAi>) => void;
  /** The server's issues; the summary and key facts show their first site-level error as is. */
  issues?: SiteSeoIssue[];
  generateSlot?: React.ReactNode;
};

type AiText = 'llms_summary' | 'llms_facts';
type AiSwitch = 'allow_search_crawlers' | 'allow_training_crawlers';

const NO_ISSUES: SiteSeoIssue[] = [];

/**
 * Site defaults, AI assistants: whether AI search and answer engines may read the site and whether AI models may
 * train on it (robots.txt rules), the llms.txt summary and key facts, and a read-only preview of the whole llms.txt
 * the server builds from the draft (its Pages section is never hand-edited).
 */
export const AiAssistantsPanel = ({
  ai,
  llmsPreview,
  onChange,
  issues = NO_ISSUES,
  generateSlot,
}: AiAssistantsPanelProps): React.ReactElement => {
  const { t } = useTranslation();

  const toggle = (field: AiSwitch, label: string): React.ReactElement => (
    <ToggleRow>
      <ToggleLabel text={label} tag="span" />
      <Toggle label={label} on={ai[field]} onToggle={() => onChange({ [field]: !ai[field] })} />
    </ToggleRow>
  );

  const textField = (field: AiText, control: (value: string) => React.ReactNode, max: number): React.ReactElement => {
    const value = ai[field] ?? '';
    const fieldIssues = issuesFor(issues, null, field);
    const error = firstErrorMessage(fieldIssues);
    const warning = error ? undefined : firstWarningMessage(fieldIssues);
    const warn = value.length > max || warning !== undefined;
    return (
      <FieldBlock data-field={field} data-tone={warn ? 'warning' : undefined} $warn={warn}>
        {control(value)}
        {PLACEHOLDER_RE.test(value) ? (
          <HintText text="Replace the [bracketed] placeholders with your own facts" tag="span" />
        ) : null}
        {error ? <ErrorText text={error} raw tag="span" /> : null}
        {warning ? <HintText text={warning} raw tag="span" /> : null}
      </FieldBlock>
    );
  };

  return (
    <Panel>
      <Head>
        <SectionTitle title="AI assistants" />
        {generateSlot}
      </Head>
      <Body>
        {toggle('allow_search_crawlers', 'AI search and answers can read your site')}
        {toggle('allow_training_crawlers', 'AI models can train on your site')}
        {textField(
          'llms_summary',
          (value) => (
            <TextArea
              label={t('Summary for AI assistants')}
              value={value}
              onChange={(next) => onChange({ llms_summary: next })}
              hint={`${value.length}/${LLMS_SUMMARY_MAX}`}
              rows={3}
            />
          ),
          LLMS_SUMMARY_MAX,
        )}
        {textField(
          'llms_facts',
          (value) => (
            <TextArea
              label={t('Key facts')}
              value={value}
              onChange={(next) => onChange({ llms_facts: next })}
              placeholder={t('- Licensed by …\n- Payouts in …')}
              hint={`${value.length}/${LLMS_FACTS_MAX}`}
              rows={5}
            />
          ),
          LLMS_FACTS_MAX,
        )}
        <Preview aria-label={t('llms.txt')}>
          <PreviewTitle text="llms.txt" raw tag="span" />
          {llmsPreview !== '' ? (
            <PreviewText>{llmsPreview}</PreviewText>
          ) : (
            <Note text="Shown after your next save" tag="span" />
          )}
          <Note text="Updated after each save" tag="span" />
        </Preview>
      </Body>
    </Panel>
  );
};

export default AiAssistantsPanel;
